import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import {
  ProductService,
  ProductServiceService,
  ProductServiceType,
  ProductServiceRequest,
} from "./productService.service";
import { AuthContext } from "../../../context/authcontext";
import { CurrencySelect } from "../../../components/currency";

interface ProductServiceFormProps {
  productService?: ProductService | null;
  onSubmit: (formData: Partial<ProductServiceRequest>) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const ProductServiceForm: React.FC<ProductServiceFormProps> = ({
  productService,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const { selectedTenantId, currencyCode } = useContext(AuthContext);
  const defaultCurrency = currencyCode || "USD";
  const appliedCurrencyRef = useRef(defaultCurrency);
  const [productServiceTypes, setProductServiceTypes] = useState<
    ProductServiceType[]
  >([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  const initialFormData = useMemo(
    () => ({
      product_n_service_name: "",
      product_n_service_description: "",
      product_n_service_unit_price: undefined,
      currency_code: "",
      is_active: true,
      product_n_service_type_id: 1,
    }),
    []
  );

  const [formData, setFormData] = useState<Partial<ProductServiceRequest>>(
    () => ({ ...initialFormData, currency_code: defaultCurrency })
  );

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchProductServiceTypes = async () => {
      try {
        setLoadingTypes(true);
        const types = await ProductServiceService.getProductServiceTypes();
        setProductServiceTypes(types);
      } catch (error) {
        console.error("Error fetching product service types:", error);
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchProductServiceTypes();
  }, []);

  useEffect(() => {
    if (productService) {
      setFormData({
        product_n_service_name: productService.product_n_service_name || "",
        product_n_service_description:
          productService.product_n_service_description || "",
        product_n_service_unit_price:
          productService.product_n_service_unit_price,
        currency_code: productService.currency_code || appliedCurrencyRef.current,
        is_active: productService.is_active ?? true,
        product_n_service_type_id:
          productService.product_n_service_type_id ||
          productService.product_n_service_type?.product_n_service_type_id ||
          1,
      });
    } else {
      setFormData({ ...initialFormData, currency_code: appliedCurrencyRef.current });
    }
  }, [productService, initialFormData]);

  useEffect(() => {
    const previousDefault = appliedCurrencyRef.current;
    appliedCurrencyRef.current = defaultCurrency;
    if (productService || previousDefault === defaultCurrency) return;
    setFormData((prev) =>
      !prev.currency_code || prev.currency_code === previousDefault
        ? { ...prev, currency_code: defaultCurrency }
        : prev
    );
  }, [defaultCurrency, productService]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.product_n_service_name?.trim()) {
      newErrors.product_n_service_name = "Product/Service name is required";
    }

    if (
      formData.product_n_service_unit_price === undefined ||
      formData.product_n_service_unit_price === null ||
      formData.product_n_service_unit_price <= 0
    ) {
      newErrors.product_n_service_unit_price =
        "Unit price must be greater than 0";
    }

    if (!formData.currency_code?.trim()) {
      newErrors.currency_code = "Currency code is required";
    }

    if (!formData.product_n_service_type_id) {
      newErrors.product_n_service_type_id = "Product/Service type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({ ...initialFormData, currency_code: appliedCurrencyRef.current });
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData = {
        ...formData,
        product_n_service_unit_price: parseFloat(
          Number(formData.product_n_service_unit_price).toFixed(4)
        ),
      };
      await onSubmit(submitData);
      if (!productService) {
        resetForm();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onCancel();
  };

  const handleInputChange = (
    field: keyof ProductServiceRequest,
    value: any
  ) => {
    if (field === "product_n_service_unit_price") {
      const numValue = value === "" ? undefined : parseFloat(value);
      setFormData((prev) => ({
        ...prev,
        [field]: numValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label htmlFor="product_n_service_name" className="form-label">
          Product/Service Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className={`form-control ${errors.product_n_service_name ? "is-invalid" : ""
            }`}
          id="product_n_service_name"
          value={formData.product_n_service_name || ""}
          onChange={(e) =>
            handleInputChange("product_n_service_name", e.target.value)
          }
          disabled={isSubmitting}
          placeholder="Enter product/service name"
        />
        {errors.product_n_service_name && (
          <div className="invalid-feedback">
            {errors.product_n_service_name}
          </div>
        )}
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <label htmlFor="product_n_service_description" className="form-label mb-0">
            Description
          </label>
          <small className="text-muted">
            {(formData.product_n_service_description || "").length} / 1000
          </small>
        </div>
        <textarea
          className="form-control"
          id="product_n_service_description"
          value={formData.product_n_service_description || ""}
          onChange={(e) =>
            handleInputChange("product_n_service_description", e.target.value)
          }
          disabled={isSubmitting}
          placeholder="Enter product/service description"
          rows={3}
          maxLength={1000}
        />
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="mb-3">
            <label
              htmlFor="product_n_service_unit_price"
              className="form-label"
            >
              Unit Price <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              className={`form-control ${errors.product_n_service_unit_price ? "is-invalid" : ""
                }`}
              id="product_n_service_unit_price"
              value={formData.product_n_service_unit_price ?? ""}
              onChange={(e) =>
                handleInputChange(
                  "product_n_service_unit_price",
                  e.target.value
                )
              }
              disabled={isSubmitting}
              placeholder="Enter unit price"
              min="0"
              step="0.0001"
              inputMode="decimal"
            />
            {errors.product_n_service_unit_price && (
              <div className="invalid-feedback">
                {errors.product_n_service_unit_price}
              </div>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <label htmlFor="currency_code" className="form-label">
              Currency Code <span className="text-danger">*</span>
            </label>
            <CurrencySelect
              value={formData.currency_code}
              onChange={(value) =>
                handleInputChange("currency_code", value || defaultCurrency)
              }
              error={errors.currency_code}
              isDisabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="product_n_service_type_id" className="form-label">
          Type <span className="text-danger">*</span>
        </label>
        {loadingTypes ? (
          <div className="form-control d-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            Loading types...
          </div>
        ) : (
          <select
            className={`form-select ${errors.product_n_service_type_id ? "is-invalid" : ""
              }`}
            id="product_n_service_type_id"
            value={formData.product_n_service_type_id || 1}
            onChange={(e) =>
              handleInputChange(
                "product_n_service_type_id",
                parseInt(e.target.value)
              )
            }
            disabled={isSubmitting}
          >
            {productServiceTypes.map((type) => (
              <option
                key={type.product_n_service_type_id}
                value={type.product_n_service_type_id}
              >
                {type.product_n_service_type_display_name}
              </option>
            ))}
          </select>
        )}
        {errors.product_n_service_type_id && (
          <div className="invalid-feedback">
            {errors.product_n_service_type_id}
          </div>
        )}
      </div>

      <div className="mb-3">
        <label htmlFor="is_active" className="form-label">
          Status
        </label>
        <div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="is_active"
              id="is_active_true"
              checked={formData.is_active === true}
              onChange={() => handleInputChange("is_active", true)}
              disabled={isSubmitting}
            />
            <label className="form-check-label" htmlFor="is_active_true">
              Active
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="is_active"
              id="is_active_false"
              checked={formData.is_active === false}
              onChange={() => handleInputChange("is_active", false)}
              disabled={isSubmitting}
            />
            <label className="form-check-label" htmlFor="is_active_false">
              Inactive
            </label>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-light"
          onClick={handleCancel}
          disabled={isSubmitting}
          data-bs-dismiss="offcanvas"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {productService ? "Updating..." : "Creating..."}
            </>
          ) : productService ? (
            "Update"
          ) : (
            "Create"
          )}
        </button>
      </div>
    </form>
  );
};

export default ProductServiceForm;
